/**
 * Blog — /business/blog
 * Article grid with featured post, category filters, and article cards.
 */
import { useState } from 'react';
import { motion } from 'framer-motion';
import { Clock, ArrowRight, BookOpen, Tag } from 'lucide-react';
import { B2BMarketingPageLayout } from '../_shared/B2BMarketingPageLayout';

/* ─────────────────────────────── data ──────────────────────────────────── */

type Category =
  | 'All'
  | 'Product'
  | 'Strategy'
  | 'AI in Hiring'
  | 'Case Studies'
  | 'Industry';

interface Article {
  slug: string;
  category: Exclude<Category, 'All'>;
  title: string;
  excerpt: string;
  readTime: string;
  date: string;
  author: string;
  authorRole: string;
  featured?: boolean;
  accentColor: string;
}

const ARTICLES: Article[] = [
  {
    slug: 'ai-video-interviews-2026',
    category: 'AI in Hiring',
    title: 'How AI Video Interviews Are Reshaping the Candidate Experience in 2026',
    excerpt:
      'Asynchronous video screening has moved from novelty to standard practice. We examine what the data says about completion rates, candidate satisfaction, and time-to-hire when AI does the first round.',
    readTime: '8 min read',
    date: '10 Apr 2026',
    author: 'Priya Sharma',
    authorRole: 'Head of Product',
    featured: true,
    accentColor: 'bg-violet-600',
  },
  {
    slug: 'explainable-role-fit-scoring',
    category: 'Product',
    title: 'Explainable Role-Fit Scoring: Why Black-Box AI Has No Place in Hiring',
    excerpt:
      "Scoring candidates without showing your work creates bias risk and destroys hiring manager trust. Here's how GoGrabJob's transparent rubric approach changes that.",
    readTime: '6 min read',
    date: '7 Apr 2026',
    author: 'Arjun Mehta',
    authorRole: 'Engineering Lead',
    accentColor: 'bg-indigo-600',
  },
  {
    slug: 'recruitment-agency-scaling',
    category: 'Strategy',
    title: '5 Operational Bottlenecks Killing Recruitment Agency Margins — and How to Fix Them',
    excerpt:
      'Recruiters spend 60% of their day on admin. We mapped the most painful drop-off points across pipeline management, client reporting, and candidate communication.',
    readTime: '7 min read',
    date: '3 Apr 2026',
    author: 'Nisha Kapoor',
    authorRole: 'Customer Success',
    accentColor: 'bg-fuchsia-600',
  },
  {
    slug: 'talentedge-case-study',
    category: 'Case Studies',
    title: 'How TalentEdge Placed 3× More Candidates Without Adding Headcount',
    excerpt:
      'A mid-size recruitment agency in Bangalore reduced their time-to-shortlist from 11 days to under 72 hours using automated screening and AI interview scoring.',
    readTime: '5 min read',
    date: '28 Mar 2026',
    author: 'Ravi Iyer',
    authorRole: 'Solutions Architect',
    accentColor: 'bg-emerald-600',
  },
  {
    slug: 'structured-interviews-bias',
    category: 'AI in Hiring',
    title: 'Structured Interviews Reduce Bias — But Only If the Rubric Is Right',
    excerpt:
      'Research shows structured interviews outperform unstructured ones on predictive validity. The catch: most rubrics are designed after the hire, not before.',
    readTime: '9 min read',
    date: '22 Mar 2026',
    author: 'Priya Sharma',
    authorRole: 'Head of Product',
    accentColor: 'bg-violet-600',
  },
  {
    slug: 'multilingual-hiring-india',
    category: 'Industry',
    title: 'The Multilingual Hiring Gap: Why English-Only ATS Platforms Fail Indian Recruiters',
    excerpt:
      'Over 60% of available Indian talent is more comfortable in a regional language. Single-language tools are quietly excluding your best candidates.',
    readTime: '6 min read',
    date: '18 Mar 2026',
    author: 'Anjali Desai',
    authorRole: 'Market Research',
    accentColor: 'bg-orange-600',
  },
  {
    slug: 'workflow-automation-ats',
    category: 'Product',
    title: 'Visual Workflow Automation in Your ATS: What to Automate First',
    excerpt:
      'Not all automation is equal. We rank the highest-ROI triggers and actions based on aggregated data from thousands of GoGrabJob pipelines.',
    readTime: '5 min read',
    date: '12 Mar 2026',
    author: 'Arjun Mehta',
    authorRole: 'Engineering Lead',
    accentColor: 'bg-indigo-600',
  },
  {
    slug: 'ats-selection-guide-2026',
    category: 'Strategy',
    title: 'The 2026 ATS Selection Guide for Growing Recruitment Teams',
    excerpt:
      'What actually matters when choosing an ATS — and how to avoid the traps of legacy enterprise software with bloated feature lists and hidden costs.',
    readTime: '11 min read',
    date: '5 Mar 2026',
    author: 'Nisha Kapoor',
    authorRole: 'Customer Success',
    accentColor: 'bg-fuchsia-600',
  },
  {
    slug: 'candidate-drop-off-rates',
    category: 'Industry',
    title: 'Why Candidates Drop Off Mid-Application — And What the Data Actually Says',
    excerpt:
      'Average application completion drops to 43% when a form takes over 5 minutes. We look at where drop-offs happen and what recruiters can do about it.',
    readTime: '7 min read',
    date: '26 Feb 2026',
    author: 'Anjali Desai',
    authorRole: 'Market Research',
    accentColor: 'bg-orange-600',
  },
];

const CATEGORIES: Category[] = [
  'All',
  'Product',
  'Strategy',
  'AI in Hiring',
  'Case Studies',
  'Industry',
];

/* ─────────────────────────────── components ─────────────────────────────── */

function AuthorAvatar({ name, color }: { name: string; color: string }) {
  const initials = name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2);
  return (
    <span
      className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-bold text-white ${color}`}
    >
      {initials}
    </span>
  );
}

function CategoryBadge({ category }: { category: Exclude<Category, 'All'> }) {
  const colours: Record<Exclude<Category, 'All'>, string> = {
    Product: 'bg-indigo-50 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300',
    Strategy: 'bg-fuchsia-50 text-fuchsia-700 dark:bg-fuchsia-900/40 dark:text-fuchsia-300',
    'AI in Hiring': 'bg-violet-50 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300',
    'Case Studies': 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
    Industry: 'bg-orange-50 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
  };
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${colours[category]}`}>
      <Tag className="h-2.5 w-2.5" />
      {category}
    </span>
  );
}

function FeaturedCard({ article }: { article: Article }) {
  return (
    <motion.article
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="group relative overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm transition-shadow hover:shadow-md dark:border-zinc-800 dark:bg-zinc-900"
    >
      {/* colour band */}
      <div className={`h-1.5 w-full ${article.accentColor}`} />
      <div className="p-8 sm:p-10">
        <div className="flex items-center gap-3">
          <span className="rounded-full bg-violet-100 px-2.5 py-0.5 text-[11px] font-semibold text-violet-700 dark:bg-violet-900/40 dark:text-violet-300">
            Featured
          </span>
          <CategoryBadge category={article.category} />
        </div>
        <h2 className="mt-4 text-2xl font-bold leading-snug tracking-tight text-gray-900 group-hover:text-violet-700 dark:text-zinc-50 dark:group-hover:text-violet-400 sm:text-3xl">
          {article.title}
        </h2>
        <p className="mt-3 text-base leading-relaxed text-gray-500 dark:text-zinc-400 sm:text-[15px]">
          {article.excerpt}
        </p>
        <div className="mt-6 flex flex-wrap items-center gap-4 border-t border-gray-100 pt-5 dark:border-zinc-800">
          <div className="flex items-center gap-2">
            <AuthorAvatar name={article.author} color={article.accentColor} />
            <div>
              <p className="text-xs font-semibold text-gray-700 dark:text-zinc-300">{article.author}</p>
              <p className="text-[11px] text-gray-400 dark:text-zinc-500">{article.authorRole}</p>
            </div>
          </div>
          <div className="ml-auto flex items-center gap-4 text-xs text-gray-400">
            <span className="flex items-center gap-1">
              <Clock className="h-3.5 w-3.5" />
              {article.readTime}
            </span>
            <span>{article.date}</span>
          </div>
          <span className="ml-auto flex items-center gap-1 text-xs font-semibold text-violet-600 group-hover:gap-2 dark:text-violet-400 transition-all">
            Read article <ArrowRight className="h-3.5 w-3.5" />
          </span>
        </div>
      </div>
    </motion.article>
  );
}

function ArticleCard({ article, idx }: { article: Article; idx: number }) {
  return (
    <motion.article
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.35, delay: 0.04 * idx }}
      className="group flex flex-col overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm transition-all hover:shadow-md dark:border-zinc-800 dark:bg-zinc-900"
    >
      <div className={`h-1 w-full ${article.accentColor}`} />
      <div className="flex flex-1 flex-col p-6">
        <CategoryBadge category={article.category} />
        <h3 className="mt-3 text-base font-bold leading-snug text-gray-900 group-hover:text-violet-700 dark:text-zinc-50 dark:group-hover:text-violet-400">
          {article.title}
        </h3>
        <p className="mt-2 flex-1 text-sm leading-relaxed text-gray-500 dark:text-zinc-400">
          {article.excerpt}
        </p>
        <div className="mt-5 flex items-center gap-3 border-t border-gray-100 pt-4 dark:border-zinc-800">
          <AuthorAvatar name={article.author} color={article.accentColor} />
          <div className="flex-1 min-w-0">
            <p className="truncate text-xs font-semibold text-gray-700 dark:text-zinc-300">{article.author}</p>
          </div>
          <div className="flex items-center gap-3 text-[11px] text-gray-400 shrink-0">
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {article.readTime}
            </span>
            <span>{article.date}</span>
          </div>
        </div>
      </div>
    </motion.article>
  );
}

/* ────────────────────────────── page ───────────────────────────────────── */

export function BlogPage() {
  const [active, setActive] = useState<Category>('All');

  const featured = ARTICLES.find((a) => a.featured)!;
  const rest = ARTICLES.filter((a) => !a.featured);

  const filtered =
    active === 'All'
      ? rest
      : rest.filter((a) => a.category === active);

  return (
    <B2BMarketingPageLayout>

      {/* ── Header ── */}
      <section
        className="relative overflow-hidden border-b border-gray-100 px-4 pb-12 pt-14 dark:border-zinc-800 sm:px-6 md:px-8 md:pt-18"
        style={{ background: 'linear-gradient(150deg, #F5F3FF 0%, #EDE9FE 35%, #F5F3FF 65%, #EEF2FF 100%)' }}
      >
        <div className="mx-auto max-w-3xl text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-violet-100">
            <BookOpen className="h-6 w-6 text-violet-600" aria-hidden />
          </div>
          <h1 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
            Blog & Insights
          </h1>
          <p className="mt-3 text-[15px] leading-relaxed text-gray-500 sm:text-base">
            Strategy, benchmarks, and product deep-dives for modern recruiting teams.
          </p>

          {/* category filter */}
          <div className="mt-8 flex flex-wrap items-center justify-center gap-2">
            {CATEGORIES.map((cat) => (
              <button
                key={cat}
                onClick={() => setActive(cat)}
                className={[
                  'rounded-full px-4 py-1.5 text-sm font-medium transition-all',
                  active === cat
                    ? 'bg-violet-600 text-white shadow-sm'
                    : 'bg-white text-gray-600 shadow-sm hover:bg-gray-50 dark:bg-zinc-800 dark:text-zinc-300 dark:hover:bg-zinc-700',
                ].join(' ')}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ── Content ── */}
      <section className="px-4 py-14 sm:px-6 md:px-8">
        <div className="mx-auto max-w-5xl space-y-10">

          {/* featured — only show when category is All */}
          {active === 'All' && <FeaturedCard article={featured} />}

          {/* article grid */}
          {filtered.length > 0 ? (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {filtered.map((article, i) => (
                <ArticleCard key={article.slug} article={article} idx={i} />
              ))}
            </div>
          ) : (
            <div className="py-20 text-center">
              <p className="text-sm text-gray-400">No articles in this category yet. Check back soon.</p>
            </div>
          )}

          {/* newsletter callout */}
          <div className="rounded-2xl bg-gradient-to-br from-violet-600 to-indigo-600 p-8 text-center text-white sm:p-10">
            <h3 className="text-xl font-bold sm:text-2xl">Never miss an insight</h3>
            <p className="mt-2 text-sm text-violet-200">
              Join 4,000+ recruiting leaders who get our fortnightly digest.
            </p>
            <div className="mx-auto mt-6 flex max-w-sm flex-col gap-3 sm:flex-row">
              <input
                type="email"
                placeholder="your@company.com"
                className="flex-1 rounded-lg border-0 bg-white/15 px-4 py-2.5 text-sm text-white placeholder-violet-300 outline-none ring-1 ring-white/25 focus:ring-2 focus:ring-white/60"
              />
              <button className="rounded-lg bg-white px-5 py-2.5 text-sm font-semibold text-violet-700 hover:bg-violet-50 transition-colors">
                Subscribe
              </button>
            </div>
            <p className="mt-3 text-[11px] text-violet-300">No spam. Unsubscribe any time.</p>
          </div>

        </div>
      </section>
    </B2BMarketingPageLayout>
  );
}
